import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Pencil, X, Loader2, ShieldCheck, Clock, CheckCircle2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending_otp: { key: 'pendingOtp', tone: 'amber', Icon: Clock },
  pending_admin_review: { key: 'pendingAdminReview', tone: 'blue', Icon: Clock },
  approved: { key: 'approved', tone: 'emerald', Icon: CheckCircle2 },
  rejected: { key: 'rejected', tone: 'red', Icon: AlertCircle },
  expired: { key: 'expired', tone: 'gray', Icon: AlertCircle },
  cancelled: { key: 'cancelled', tone: 'gray', Icon: X },
};

const toneClasses = {
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  gray: 'bg-gray-50 text-gray-700 border-gray-200',
};

const ContactChangeModal = ({ open, changeType, currentValue, sessionToken, onClose, onSubmitted }) => {
  const [step, setStep] = useState(1); // 1 = enter new value + password, 2 = OTP
  const [newValue, setNewValue] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setStep(1); setNewValue(''); setPassword(''); setOtp(''); setRequestId(null); setBusy(false); setError(null);
    }
  }, [open]);

  if (!open) return null;

  const headers = { Authorization: `Bearer ${sessionToken}` };
  const label = changeType === 'email' ? 'email address' : 'phone number';

  const submitStart = async () => {
    setBusy(true); setError(null);
    try {
      const res = await axios.post(
        `${API}/profile/contact-change/start`,
        { change_type: changeType, new_value: newValue, current_password: password },
        { headers },
      );
      setRequestId(res.data.request_id);
      setStep(2);
    } catch (e) {
      setError(e?.response?.data?.detail || `Failed to start ${changeType} change`);
    } finally { setBusy(false); }
  };

  const submitVerify = async () => {
    setBusy(true); setError(null);
    try {
      await axios.post(
        `${API}/profile/contact-change/verify-otp`,
        { request_id: requestId, otp_code: otp },
        { headers },
      );
      onSubmitted?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Invalid OTP');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid={`contact-change-modal-${changeType}`}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 p-1 text-gray-500 hover:text-gray-800" data-testid="contact-change-close-btn">
          <X size={20} />
        </button>

        <h3 className="text-xl font-semibold text-gray-900 mb-1">Change {label}</h3>
        <p className="text-sm text-gray-500 mb-5">
          {step === 1
            ? 'For security, every change requires your password and admin approval.'
            : `We sent a 6-digit code to ${newValue}. Enter it below.`}
        </p>

        {step === 1 && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Current {label}</label>
              <input
                value={currentValue || ''}
                disabled
                className="w-full rounded-lg border-gray-200 bg-gray-50 px-3 py-2 text-gray-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">New {label}</label>
              <input
                type={changeType === 'email' ? 'email' : 'tel'}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder={changeType === 'email' ? 'name@example.com' : '+96512345678'}
                className="w-full rounded-lg border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                data-testid={`contact-change-new-${changeType}`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Confirm with your current password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                data-testid="contact-change-password"
              />
            </div>

            {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

            <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
              <ShieldCheck size={14} />
              You'll receive an OTP. After verification, an admin will review the change.
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">Cancel</button>
              <button
                onClick={submitStart}
                disabled={busy || !newValue || !password}
                className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                data-testid="contact-change-send-otp-btn"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}Send OTP
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">6-digit code</label>
              <input
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className="w-full rounded-lg border-gray-300 px-3 py-2 text-lg tracking-widest text-center focus:ring-2 focus:ring-blue-500"
                data-testid="contact-change-otp-input"
              />
            </div>

            {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

            <div className="flex justify-between pt-2">
              <button onClick={() => setStep(1)} className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">Back</button>
              <button
                onClick={submitVerify}
                disabled={busy || otp.length !== 6}
                className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                data-testid="contact-change-verify-otp-btn"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}Verify & submit
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PendingChip = ({ request }) => {
  const { t } = useTranslation();
  const cfg = STATUS_LABEL[request.status] || STATUS_LABEL.pending_admin_review;
  const Icon = cfg.Icon;
  return (
    <div
      className={`mt-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${toneClasses[cfg.tone]}`}
      title={`${t('profile.contact.requested')}: ${request.requested_value}`}
      data-testid={`contact-change-status-${request.change_type}`}
    >
      <Icon size={12} />
      <span>{t(`profile.contact.status.${cfg.key}`)}: {request.requested_value}</span>
    </div>
  );
};

const ContactInfoChange = ({ user, sessionToken, onChanged }) => {
  const { t } = useTranslation();
  const [openType, setOpenType] = useState(null); // 'email' | 'phone' | null
  const [requests, setRequests] = useState([]);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/profile/contact-change/my-requests`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      setRequests(res.data?.requests || []);
    } catch (_) { /* silent */ }
  };

  useEffect(() => { if (sessionToken) fetchRequests(); }, [sessionToken]);

  const pendingFor = (type) =>
    requests.find(r => r.change_type === type && (r.status === 'pending_otp' || r.status === 'pending_admin_review'));

  const cancelRequest = async (id) => {
    if (!window.confirm(t('profile.contact.confirmCancel'))) return;
    try {
      await axios.post(`${API}/profile/contact-change/${id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      fetchRequests();
    } catch (_) { /* silent */ }
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-info-editable">
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">{t('profile.fields.email')}</label>
          <div className="flex items-center gap-2">
            <p className="text-gray-900 break-all">{user.email}</p>
            <button
              onClick={() => setOpenType('email')}
              disabled={!!pendingFor('email')}
              className="ml-auto inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              data-testid="edit-email-btn"
              title={pendingFor('email') ? t('profile.contact.pendingHint') : t('profile.contact.changeEmailHint')}
            >
              <Pencil size={14} /> {t('profile.contact.change')}
            </button>
          </div>
          {pendingFor('email') && (
            <div className="flex items-center gap-2 flex-wrap">
              <PendingChip request={pendingFor('email')} />
              <button onClick={() => cancelRequest(pendingFor('email').id)} className="text-xs text-gray-500 hover:text-red-600">{t('profile.contact.cancel')}</button>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">{t('profile.fields.phone')}</label>
          <div className="flex items-center gap-2">
            <p className="text-gray-900">{user.phone || '—'}</p>
            <button
              onClick={() => setOpenType('phone')}
              disabled={!!pendingFor('phone')}
              className="ml-auto inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              data-testid="edit-phone-btn"
              title={pendingFor('phone') ? t('profile.contact.pendingHint') : t('profile.contact.changePhoneHint')}
            >
              <Pencil size={14} /> {t('profile.contact.change')}
            </button>
          </div>
          {pendingFor('phone') && (
            <div className="flex items-center gap-2 flex-wrap">
              <PendingChip request={pendingFor('phone')} />
              <button onClick={() => cancelRequest(pendingFor('phone').id)} className="text-xs text-gray-500 hover:text-red-600">{t('profile.contact.cancel')}</button>
            </div>
          )}
        </div>
      </div>

      <ContactChangeModal
        open={openType === 'email'}
        changeType="email"
        currentValue={user.email}
        sessionToken={sessionToken}
        onClose={() => setOpenType(null)}
        onSubmitted={() => { fetchRequests(); onChanged?.(); }}
      />
      <ContactChangeModal
        open={openType === 'phone'}
        changeType="phone"
        currentValue={user.phone}
        sessionToken={sessionToken}
        onClose={() => setOpenType(null)}
        onSubmitted={() => { fetchRequests(); onChanged?.(); }}
      />
    </>
  );
};

export default ContactInfoChange;
